import { ApiError } from "@gjwtrack/client";
import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import { NewIssueDialog } from "./components/NewIssueDialog.tsx";
import { Shell } from "./components/Shell.tsx";
import { RingAlerts } from "./components/RingAlerts.tsx";
import { client, useClient } from "./lib/client.ts";
import { issueKey } from "./lib/format.ts";
import { AccountPage } from "./pages/AccountPage.tsx";
import { ReleasesPage } from "./pages/ReleasesPage.tsx";
import { AdminPage } from "./pages/AdminPage.tsx";
import { BoardPage } from "./pages/BoardPage.tsx";
import { BoardRedirect, BoardsEntry, BoardsPage } from "./pages/BoardsPage.tsx";
import { DashboardPage, DashboardsIndex } from "./pages/DashboardPage.tsx";
import { IssuePage } from "./pages/IssuePage.tsx";
import { IssuesPage } from "./pages/IssuesPage.tsx";
import { KnowledgeBasePage } from "./pages/KnowledgeBasePage.tsx";
import { LoginPage } from "./pages/LoginPage.tsx";

type Session = "loading" | "in" | "out";

export function App() {
  const [session, setSession] = useState<Session>("loading");
  const [creating, setCreating] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const schema = useClient((s) => s.schema);

  useEffect(() => {
    let cancelled = false;
    client
      .start()
      .then(() => !cancelled && setSession("in"))
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) setSession("out");
        else client.pushError("startup", (e as Error).message);
      });
    return () => {
      cancelled = true;
      client.stop();
    };
  }, []);

  const connection = useClient((s) => s.connection);
  useEffect(() => {
    if (connection === "unauthorized") setSession("out");
  }, [connection]);

  if (session === "loading") return <Centered>Loading…</Centered>;
  if (session === "out") {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage onLoggedIn={() => setSession("in")} />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname + location.search }} />} />
      </Routes>
    );
  }
  return (
    <Shell
      onCreate={() => setCreating(true)}
      onLoggedOut={() => {
        setSession("out");
        navigate("/login");
      }}
    >
      <Routes>
        <Route path="/" element={<Navigate to="/issues" replace />} />
        <Route path="/login" element={<Navigate to="/issues" replace />} />
        <Route path="/issues" element={<IssuesPage />} />
        <Route path="/issue/:key" element={<IssuePage />} />
        <Route path="/dashboards" element={<DashboardsIndex />} />
        <Route path="/dashboard/:id" element={<DashboardPage />} />
        {/* YouTrack's URLs: /agiles → last board, /agiles/:id/current | :sprintId | all | backlog. Old /boards and /board/:id links keep working. */}
        <Route path="/agiles" element={<BoardsEntry />} />
        <Route path="/agiles/all" element={<BoardsPage />} />
        <Route path="/agiles/:id" element={<BoardRedirect />} />
        <Route path="/agiles/:id/:sprint" element={<BoardPage />} />
        <Route path="/boards" element={<Navigate to="/agiles" replace />} />
        <Route path="/boards/all" element={<Navigate to="/agiles/all" replace />} />
        <Route path="/board/:id" element={<BoardRedirect />} />
        <Route path="/articles" element={<KnowledgeBasePage view="home" />} />
        <Route path="/articles/new" element={<KnowledgeBasePage view="new" />} />
        <Route path="/article/:key" element={<KnowledgeBasePage view="article" />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/releases" element={<ReleasesPage />} />
        <Route path="/admin/*" element={<AdminPage />} />
        <Route path="*" element={<Centered>Nothing here.</Centered>} />
      </Routes>
      <RingAlerts />
      {creating && (
        <NewIssueDialog
          onClose={() => setCreating(false)}
          onCreated={(issue) => {
            setCreating(false);
            navigate(`/issue/${issueKey(schema, issue)}`);
          }}
        />
      )}
    </Shell>
  );
}

export function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex h-full items-center justify-center text-muted">{children}</div>;
}
